import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, BookOpenCheck } from "lucide-react";
import { CHART_REGISTRY, getChart } from "@/pages/ChartsHub";

// Standalone renderer for a single Reference Chart. Reads the registry so
// the hub, the topic-detail pages, and this viewer stay perfectly in sync.
export default function ChartViewer() {
  const { chartId } = useParams();
  const entry = useMemo(() => getChart(chartId), [chartId]);

  if (!entry) {
    // Unknown id → send back to the hub rather than a 404 page.
    return <Navigate to="/app/reference" replace />;
  }

  const Chart = entry.component;

  return (
    <div className="max-w-6xl mx-auto" data-testid={`chart-viewer-${entry.id}`}>
      <div className="mb-4">
        <Link
          to="/app/reference"
          className="inline-flex items-center gap-1.5 text-sm hover:underline"
          style={{ color: "var(--text-secondary)" }}
          data-testid="chart-viewer-back"
        >
          <ArrowLeft size={14} /> All reference charts
        </Link>
      </div>

      <div className="mb-5 flex items-center gap-2 flex-wrap">
        <span
          className="text-[11px] font-semibold uppercase tracking-wide"
          style={{ color: entry.subjectColor }}
        >
          {entry.subject}
        </span>
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          · {entry.count} entries
        </span>
      </div>

      <Chart />

      {entry.topicIds?.length > 0 && (
        <div className="pm-card p-5" data-testid="chart-viewer-related-topics">
          <div className="flex items-center gap-2 mb-3">
            <BookOpenCheck size={16} style={{ color: "var(--primary)" }} />
            <div className="font-semibold text-sm">
              Study the underlying MCAT topics
            </div>
          </div>
          <p
            className="text-xs mb-3"
            style={{ color: "var(--text-secondary)" }}
          >
            This chart lives on the following concept pages — each includes an
            AI-generated analogy, story, and practice questions.
          </p>
          <div className="flex flex-wrap gap-2">
            {entry.topicIds.map((tid) => (
              <Link
                key={tid}
                to={`/app/topic/${tid}`}
                className="pm-btn-secondary text-xs"
                data-testid={`chart-viewer-topic-${tid}`}
              >
                {tid
                  .replace(/^medpath-/, "")
                  .replace(/-/g, " ")
                  .replace(/\b\w/g, (m) => m.toUpperCase())}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export the registry so other pages can import from a stable place.
export { CHART_REGISTRY };
